import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import SalesPersonAutocomplete from '../Common/SalesPersonAutocomplete';
import { makeApiCall } from '../../config/api';

const CustomerForm = ({ customer, onSuccess, onCancel, lookups }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // TKT-183: per-field validation errors and a list of fields the user
  // already touched, so we don't blast required-field warnings on a
  // brand new form before the user has had a chance to type.
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email_address: '',
    email_address_2: '',
    phone_number: '',
    mobile_number: '',
    fax: '',
    ext: '',
    job_title: '',
    street_address: '',
    city: '',
    province: '',
    postal_code: '',
    customer_type: 'residential',
    sales_person_id: '',
    commercial_sales_person_id: '',
    construction_sales_person_id: '',
    assigned_sales_person_id: '',
    lead_source_id: '',
    property_type_id: '',
    group_id: '',
    is_signed_client: false,
    is_signed_for_snow: false,
    auto_renew_contact: false,
    renewed_contact: false,
    quoted: false,
    is_active: true,
    web_form_content: ''
  });

  // Initialize form with customer data if editing
  useEffect(() => {
    if (customer) {
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        company_name: customer.company_name || '',
        email_address: customer.email_address || '',
        email_address_2: customer.email_address_2 || '',
        phone_number: customer.phone_number || '',
        mobile_number: customer.mobile_number || '',
        fax: customer.fax || '',
        ext: customer.ext || '',
        job_title: customer.job_title || '',
        street_address: customer.street_address || '',
        city: customer.city || '',
        province: customer.province || '',
        postal_code: customer.postal_code || '',
        customer_type: customer.customer_type || 'residential',
        sales_person_id: customer.sales_person_id || '',
        commercial_sales_person_id: customer.commercial_sales_person_id || '',
        construction_sales_person_id: customer.construction_sales_person_id || '',
        assigned_sales_person_id: customer.assigned_sales_person_id || '',
        lead_source_id: customer.lead_source_id || '',
        property_type_id: customer.property_type_id || '',
        group_id: customer.group_id || '',
        is_signed_client: customer.is_signed_client || false,
        is_signed_for_snow: customer.is_signed_for_snow || false,
        auto_renew_contact: customer.auto_renew_contact || false,
        renewed_contact: customer.renewed_contact || false,
        quoted: customer.quoted || false,
        is_active: customer.is_active !== undefined ? customer.is_active : true,
        web_form_content: customer.web_form_content || ''
      });
    }
  }, [customer]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear the per-field error as soon as the user types/changes that
    // field; full re-validation runs again on submit.
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  // TKT-183: Naomi requested stronger required-field validation for the
  // Create Customer form. Company is only required when the customer is
  // not residential (see TKT-479 below for which types). Postal
  // code is intentionally optional per the ticket.
  // TKT-479: Naomi (Oct 23) - only Commercial / Industrial and Multi-Residential
  // need a company name. Residential and Construction (often homeowners) don't.
  // Compare case-insensitively so legacy values like "Residential" behave.
  const COMPANY_REQUIRED_TYPES = ['commercial', 'multi_residential'];
  const requiresCompanyName = (data) =>
    !!data && COMPANY_REQUIRED_TYPES.includes(String(data.customer_type || '').trim().toLowerCase());

  const validateForm = (data) => {
    const errors = {};
    const requireString = (key, message) => {
      if (!data[key] || !String(data[key]).trim()) {
        errors[key] = message;
      }
    };
    requireString('first_name', t('crm.firstNameRequired', 'First name is required'));
    requireString('last_name', t('crm.lastNameRequired', 'Last name is required'));
    requireString('email_address', t('crm.emailRequired', 'Email address is required'));
    if (
      data.email_address
      && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(data.email_address).trim())
    ) {
      errors.email_address = t('crm.emailInvalid', 'Enter a valid email address');
    }
    requireString('phone_number', t('crm.phoneRequired', 'Phone number is required'));
    requireString('street_address', t('crm.streetAddressRequired', 'Street address is required'));
    requireString('city', t('crm.cityRequired', 'City is required'));
    if (!data.lead_source_id) {
      errors.lead_source_id = t('crm.leadSourceRequired', 'Lead source is required');
    }
    if (!data.group_id) {
      errors.group_id = t('crm.customerGroupRequired', 'Customer group is required');
    }
    if (requiresCompanyName(data) && (!data.company_name || !String(data.company_name).trim())) {
      errors.company_name = t('crm.companyNameRequiredNonResidential',
        'Company name is required for commercial, multi-residential, and industrial customers');
    }
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errors = validateForm(formData);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError(t('crm.fixHighlightedFields', 'Please fix the highlighted fields below.'));
      return;
    }
    setLoading(true);
    setError('');

    try {
      const endpoint = customer 
        ? `api/customers/${customer.id}`
        : 'api/customers';
      
      const method = customer ? 'PUT' : 'POST';

      const response = await makeApiCall(endpoint, {
        method,
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save customer');
      }

      const data = await response.json();
      onSuccess(data.message || 'Customer saved successfully');

    } catch (error) {
      console.error('Error saving customer:', error);
      setError(error.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const companyRequired = requiresCompanyName(formData);

  // Canadian provinces
  const provinces = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
    'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia',
    'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec',
    'Saskatchewan', 'Yukon'
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Personal Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            pb: 1,
            mb: 3
          }}
        >
          {t('crm.contactInformation')}
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              name="first_name"
              label={t('crm.firstName')}
              value={formData.first_name}
              onChange={handleChange}
              error={!!fieldErrors.first_name}
              helperText={fieldErrors.first_name || ' '}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              name="last_name"
              label={t('crm.lastName')}
              value={formData.last_name}
              onChange={handleChange}
              error={!!fieldErrors.last_name}
              helperText={fieldErrors.last_name || ' '}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required={companyRequired}
              fullWidth
              name="company_name"
              label={`${t('crm.companyName')}${companyRequired ? ' *' : ''}`}
              value={formData.company_name}
              onChange={handleChange}
              error={!!fieldErrors.company_name}
              helperText={
                fieldErrors.company_name
                || (companyRequired ? t('crm.companyNameRequiredHelper', 'Required for non-residential customers') : ' ')
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              name="job_title"
              label={t('crm.jobTitle')}
              value={formData.job_title}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Contact Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            pb: 1,
            mb: 3
          }}
        >
          {t('crm.contactInformation')}
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              name="email_address"
              label={t('crm.primaryEmail')}
              type="email"
              value={formData.email_address}
              onChange={handleChange}
              error={!!fieldErrors.email_address}
              helperText={fieldErrors.email_address || ' '}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              name="email_address_2"
              label={t('crm.secondaryEmail')}
              type="email"
              value={formData.email_address_2}
              onChange={handleChange}
              helperText={t('crm.optionalHelper', 'Optional')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              required
              fullWidth
              name="phone_number"
              label={t('crm.phoneNumber')}
              value={formData.phone_number}
              onChange={handleChange}
              error={!!fieldErrors.phone_number}
              helperText={fieldErrors.phone_number || ' '}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              name="mobile_number"
              label={t('crm.mobileNumber')}
              value={formData.mobile_number}
              onChange={handleChange}
              helperText={t('crm.optionalHelper', 'Optional')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
              fullWidth
              name="ext"
              label={t('crm.extension')}
              value={formData.ext}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField
              fullWidth
              name="fax"
              label={t('crm.fax')}
              value={formData.fax}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Address Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            pb: 1,
            mb: 3
          }}
        >
          {t('crm.addressInformation')}
        </Typography>
        <Grid container spacing={3}>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              name="street_address"
              label={t('crm.streetAddress')}
              value={formData.street_address}
              onChange={handleChange}
              error={!!fieldErrors.street_address}
              helperText={fieldErrors.street_address || ' '}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              required
              fullWidth
              name="city"
              label={t('crm.city')}
              value={formData.city}
              onChange={handleChange}
              error={!!fieldErrors.city}
              helperText={fieldErrors.city || ' '}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>{t('crm.province')}</InputLabel>
              <Select
                name="province"
                value={formData.province}
                label={t('crm.province')}
                onChange={handleChange}
              >
                {provinces.map((province) => (
                  <MenuItem key={province} value={province}>
                    {province}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              name="postal_code"
              label={t('crm.postalCode')}
              value={formData.postal_code}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Business Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            pb: 1,
            mb: 3
          }}
        >
          {t('crm.businessInformation')}
        </Typography>
        <Grid container spacing={3}>
          {/* Customer Type */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel>Customer Type</InputLabel>
              <Select
                name="customer_type"
                value={formData.customer_type}
                label={t('crm.customerType')}
                onChange={handleChange}
              >
                <MenuItem value="residential">{t('crm.customerTypes.residential', 'Residential')}</MenuItem>
                <MenuItem value="multi_residential">{t('crm.customerTypes.multiResidential', 'Multi-Residential')}</MenuItem>
                <MenuItem value="commercial">{t('crm.customerTypes.commercialIndustrial', 'Commercial / Industrial')}</MenuItem>
                <MenuItem value="construction">{t('crm.customerTypes.construction', 'Construction')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Lead Source */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required error={!!fieldErrors.lead_source_id} sx={{ minWidth: 200 }}>
              <InputLabel>{t('crm.leadSource')}</InputLabel>
              <Select
                name="lead_source_id"
                value={formData.lead_source_id}
                label={t('crm.leadSource')}
                onChange={handleChange}
              >
                <MenuItem value="">{t('crm.selectLeadSource', 'Select Lead Source')}</MenuItem>
                {lookups?.lead_sources?.map((ls) => (
                  <MenuItem key={ls.id} value={ls.id}>
                    {ls.name}
                  </MenuItem>
                )) || []}
              </Select>
              {fieldErrors.lead_source_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {fieldErrors.lead_source_id}
                </Typography>
              )}
            </FormControl>
          </Grid>
          
          {/* Property Type */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel>Property Type</InputLabel>
              <Select
                name="property_type_id"
                value={formData.property_type_id}
                label={t('crm.propertyType')}
                onChange={handleChange}
              >
                <MenuItem value="">Select Property Type</MenuItem>
                {lookups?.property_types?.map((pt) => (
                  <MenuItem key={pt.id} value={pt.id}>
                    {pt.name}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Customer Group */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required error={!!fieldErrors.group_id} sx={{ minWidth: 200 }}>
              <InputLabel>{t('crm.customerGroup')}</InputLabel>
              <Select
                name="group_id"
                value={formData.group_id}
                label={t('crm.customerGroup')}
                onChange={handleChange}
              >
                <MenuItem value="">{t('crm.selectCustomerGroup', 'Select Customer Group')}</MenuItem>
                {lookups?.customer_groups?.map((cg) => (
                  <MenuItem key={cg.id} value={cg.id}>
                    {cg.name}
                  </MenuItem>
                )) || []}
              </Select>
              {fieldErrors.group_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {fieldErrors.group_id}
                </Typography>
              )}
            </FormControl>
          </Grid>
          
          {/* Sales Person */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <SalesPersonAutocomplete
              name="sales_person_id"
              label={t('crm.salesPerson')}
              value={formData.sales_person_id}
              onChange={handleChange}
              sx={{ minWidth: 200 }}
            />
          </Grid>
          
          {/* Commercial Sales Person */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <SalesPersonAutocomplete
              name="commercial_sales_person_id"
              label={t('crm.salesPersonCommercial')}
              value={formData.commercial_sales_person_id}
              onChange={handleChange}
              sx={{ minWidth: 200 }}
            />
          </Grid>
          
          {/* Construction Sales Person */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <SalesPersonAutocomplete
              name="construction_sales_person_id"
              label={t('crm.salesPersonConstruction')}
              value={formData.construction_sales_person_id}
              onChange={handleChange}
              sx={{ minWidth: 200 }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Status & Flags Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            pb: 1,
            mb: 3
          }}
        >
          Status & Flags
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_signed_client}
                  onChange={handleChange}
                  name="is_signed_client"
                />
              }
              label={t('crm.signedClient')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_signed_for_snow}
                  onChange={handleChange}
                  name="is_signed_for_snow"
                />
              }
              label="Signed for Snow Services"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.auto_renew_contact}
                  onChange={handleChange}
                  name="auto_renew_contact"
                />
              }
              label={t('crm.autoRenewContact')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.quoted}
                  onChange={handleChange}
                  name="quoted"
                />
              }
              label={t('crm.hasQuotes')}
            />
          </Grid>
          {customer && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.renewed_contact}
                      onChange={handleChange}
                      name="renewed_contact"
                    />
                  }
                  label={t('crm.renewedContact')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleChange}
                      name="is_active"
                    />
                  }
                  label={t('crm.activeCustomer')}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Notes Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              name="web_form_content"
              label="Notes / Web Form Content"
              value={formData.web_form_content}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Form Actions */}
      <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 2 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? t('tasks.saving') : (customer ? t('crm.updateCustomer') : t('crm.createCustomer'))}
        </Button>
      </Box>
    </Box>
  );
};

export default CustomerForm; 